import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { SidebarNavigationModalConfig as SidebarNavigationModalContentConfig } from '@front-platform/content-builder';
import { sidebarNavigationModalResolveConfig } from '@front-platform/content-builder';
import { from, Observable, Subject, timer } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Component, OnDestroy, OnInit, computed, input } from '@angular/core';

import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';

import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';

import { ISidebarNavigationItem } from '@shared/models/sidebar-navigation-item.model';

import { UserService } from '@core/services/user/user.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { NativeService } from '@core/services/native/native.service';
import { ResetStateService } from '@core/services/reset-state.service';
import { ShopSearchParametersService } from '@core/services/shop-search-parameters/shop-search-parameters.service';

import { WaitingModalComponent } from '@shared/modals/waiting-modal/waiting-modal.component';
import { AuthenticationModalComponent } from '@shared/modals/authentication-modal/authentication-modal.component';
import { CartParametersModalComponent } from '@shared/modals/cart-parameters-modal/cart-parameters-modal.component';
import { getDoodLogoUrl } from '@common/i18n/dood-logo-url';
import { getDoodBrand } from '@common/i18n/get-dood-brand';
import { toSignal } from '@angular/core/rxjs-interop';
import { ModalConfig } from '@core/services/modals/modals-util.type';

declare global {
  interface Window {
    doodWebappVersion?: string;
  }
}

function coerceToBooleanWithDefaultTrue(value: boolean | undefined): boolean {
  return value ?? true;
}

export type SidebarNavigationModalConfig = ModalConfig<{
  handle: 'sidebar-navigation-modal';
  inputs: {};
}>;
export interface SidebarNavigationModalRuntimeConfig {
  'heading'?: string;
  'custom_items'?: any[];
}

export type SidebarNavigationModalConfigInput = Partial<
  Omit<SidebarNavigationModalContentConfig, keyof SidebarNavigationModalRuntimeConfig> &
    SidebarNavigationModalRuntimeConfig
>;
export type SidebarNavigationModalResolvedConfig = Omit<
  SidebarNavigationModalContentConfig,
  keyof SidebarNavigationModalRuntimeConfig
> &
  Partial<SidebarNavigationModalRuntimeConfig>;
export interface SidebarNavigationModalContract extends ConfigComponentContract<
  SidebarNavigationModalConfigInput,
  SidebarNavigationModalResolvedConfig
> {}

@Component({
  selector: 'app-sidebar-navigation-modal',
  templateUrl: './sidebar-navigation-modal.component.html',
  standalone: false,
})
export class SidebarNavigationModalComponent
  implements OnInit, OnDestroy, SidebarNavigationModalContract
{
  readonly config = input<SidebarNavigationModalConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    SidebarNavigationModalContentConfig,
    SidebarNavigationModalRuntimeConfig
  >(sidebarNavigationModalResolveConfig, () => ({ config: this.config() }));

  constructor(
    private authSelector: AuthStoreSelector,
    private modalSelector: ModalStoreSelector,
    private readonly userService: UserService,
    private readonly modalsService: ModalsService,
    private settingsSelector: SettingsStoreSelector,
    private shopSelector: ShopStoreSelector,
    private marketplaceSelector: MarketplaceStoreSelector,
    private readonly resetStateService: ResetStateService,
    private readonly shopSearchParametersService: ShopSearchParametersService,
  ) {}

  static handle = 'sidebar-navigation-modal' as const;
  resetTapCount = 0;
  readonly heading = computed(() => this.resolvedConfig()['heading'] as unknown as string);
  readonly customItems = computed(() => this.resolvedConfig()['custom_items'] as unknown as any[]);
  readonly displayPoweredByDood = computed(
    () => this.resolvedConfig()['display_powered_by_dood'] as unknown as boolean,
  );
  readonly footerMessage = computed(
    () => this.resolvedConfig()['footer_message'] as unknown as string,
  );
  readonly footerMessageUrl = computed(
    () => this.resolvedConfig()['footer_message_url'] as unknown as string,
  );
  readonly cgvUrl = computed(() => this.resolvedConfig()['cgv_url'] as unknown as string);
  readonly privacyPolicyUrl = computed(
    () => this.resolvedConfig()['privacy_policy_url'] as unknown as string,
  );
  readonly legalNoticeUrl = computed(
    () => this.resolvedConfig()['legal_notice_url'] as unknown as string,
  );
  // ! The displayHomeLink may be undefined (because this option was added later to the content-builder), so we use a custom coercion function to force to be true if not defined
  readonly displayHomeLink = computed(() =>
    coerceToBooleanWithDefaultTrue(
      this.resolvedConfig()['display_home_link'] as boolean | undefined,
    ),
  );
  readonly displaySeeAllDistributionModes = computed(() =>
    coerceToBooleanWithDefaultTrue(
      this.resolvedConfig()['display_see_all_distribution_modes'] as boolean | undefined,
    ),
  );

  private readonly loggedInItems: ISidebarNavigationItem[] = [
    {
      title: 'sidebar.navigation.home',
      path: '/',
      icon: 'arrow-left',
      type: 'link',
    },
    {
      title: 'sidebar.navigation.account',
      path: '/account',
      icon: 'account',
      type: 'link',
    },
    {
      title: 'sidebar.navigation.orders',
      path: '/orders',
      icon: 'todo',
      type: 'link',
    },
    {
      title: 'sidebar.navigation.checks',
      path: '/checks',
      icon: 'receipt_long',
      type: 'link',
    },
  ];

  private readonly notLoggedInItems: ISidebarNavigationItem[] = [
    {
      title: 'sidebar.navigation.home',
      path: '/',
      icon: 'arrow-left',
      type: 'link',
    },
  ];

  protected readonly doodLogo = getDoodLogoUrl();
  protected readonly doodBrand = getDoodBrand();

  index$: Observable<number> = this.modalSelector
    .selectModal(SidebarNavigationModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 4)));

  userId$ = this.authSelector.selectUserId;
  firebaseToken$ = this.authSelector.selectStatusToken;
  isAnonymous$ = this.authSelector.selectUserIsAnonymous;
  forceDistributionMode$ = this.settingsSelector.selectParameters$.pipe(
    map(parameters => parameters.force_distribution_mode),
  );
  locales$ = this.marketplaceSelector.selectMarketplaceLocales;

  isNotched = false;

  isLogoutInProgress = false;

  protected readonly defaultItems = computed<ISidebarNavigationItem[]>(() => {
    let defaultItems =
      this.userChange() && !this.userService.user?.is_anonymous
        ? this.loggedInItems
        : this.notLoggedInItems;
    // Only show the "table checks" entry when the active shop exposes the pay-at-table plugin
    if (!this.hasPayAtTablePlugin()) {
      defaultItems = defaultItems.filter(item => item.path !== '/checks');
    }
    if (!this.displayHomeLink()) {
      return defaultItems.filter(item => item.path !== '/');
    }
    return defaultItems;
  });

  private readonly hasPayAtTablePlugin = computed<boolean>(() => {
    const activeShop = this.shopSelector.selectActive();
    return (
      activeShop?.plugins?.some(
        plugin => plugin.plugin_enabled && plugin.type === 'PAY_AT_TABLE',
      ) ?? false
    );
  });

  doodCorpoUrl = 'https://www.dood.com/corporate/';
  version = window.doodWebappVersion ? window.doodWebappVersion.substring(0, 7) : 'Unknown';

  private readonly destroyed$ = new Subject<boolean>();

  private readonly userChange = toSignal(this.userId$);

  ngOnInit(): void {
    from(NativeService.isNotched())
      .pipe(take(1))
      .subscribe(async _isNotched => {
        this.isNotched = _isNotched;

        if (this.isNotched) {
          await StatusBar.setStyle({ style: Style.Dark });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  close(): void {
    // Tiny delay to prevent page change "flash"
    timer(10).subscribe(() => {
      this.modalsService.close(SidebarNavigationModalComponent.handle);
    });
  }

  openAuthenticationModal(): void {
    this.close();
    this.modalsService.open(AuthenticationModalComponent.handle);
  }

  async logout(): Promise<any> {
    this.modalsService.open(WaitingModalComponent.handle);
    this.isLogoutInProgress = true;
    this.resetStateService.refreshUI().subscribe();
  }

  async resetTap(): Promise<void> {
    this.resetTapCount++;
    if (this.resetTapCount > 5) {
      this.isLogoutInProgress = true;
      this.resetStateService.refreshUI().subscribe();
    }
  }

  resetForceDistributionMode(): void {
    this.shopSearchParametersService.setForceDistributionMode(undefined);
    this.modalsService.open(CartParametersModalComponent.handle);
    this.close();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(SidebarNavigationModalComponent, {
  'keyAliases': {},
});
